import { useState, useEffect, useCallback } from "react";
import type { TweakSettings } from "../types/sigwaz";
import { DEFAULT_TWEAKS } from "../types/sigwaz";

const STORAGE_KEY = "sigwaz-tweaks";

const VARIANTS: Record<string, Record<string, string>> = {
  glacier: {
    "--bg": "#020f1c", "--bg-rgb": "2,15,28",
    "--s0": "#041e30", "--s1": "#062840",
    "--accent": "#0ea5e9", "--accent2": "#67e8f9", "--accent-rgb": "14,165,233",
    "--t1": "#ecfeff", "--t2": "#94c5d4", "--t3": "#3a7080",
    "--bd": "rgba(34,211,238,.1)", "--bdb": "rgba(34,211,238,.2)",
    "--card": "rgba(34,211,238,.03)",
  },
  midnight: {
    "--bg": "#020817", "--bg-rgb": "2,8,23",
    "--s0": "#071428", "--s1": "#0a1f3d",
    "--accent": "#0ea5e9", "--accent2": "#818cf8", "--accent-rgb": "14,165,233",
    "--t1": "#f1f5f9", "--t2": "#94a3b8", "--t3": "#475569",
    "--bd": "rgba(255,255,255,.07)", "--bdb": "rgba(255,255,255,.14)",
    "--card": "rgba(255,255,255,.028)",
  },
  obsidian: {
    "--bg": "#050505", "--bg-rgb": "5,5,5",
    "--s0": "#111", "--s1": "#1a1a1a",
    "--accent": "#f97316", "--accent2": "#fbbf24", "--accent-rgb": "249,115,22",
    "--t1": "#fafafa", "--t2": "#a1a1aa", "--t3": "#52525b",
    "--bd": "rgba(255,255,255,.08)", "--bdb": "rgba(255,255,255,.16)",
    "--card": "rgba(255,255,255,.035)",
  },
  nebula: {
    "--bg": "#0d0418", "--bg-rgb": "13,4,24",
    "--s0": "#160826", "--s1": "#1e0d36",
    "--accent": "#c084fc", "--accent2": "#f472b6", "--accent-rgb": "192,132,252",
    "--t1": "#fdf4ff", "--t2": "#c4a8d8", "--t3": "#7c5cbf",
    "--bd": "rgba(192,132,252,.09)", "--bdb": "rgba(192,132,252,.18)",
    "--card": "rgba(192,132,252,.03)",
  },
};

function hexToRgb(hex: string): string {
  const n = parseInt(hex.replace("#", ""), 16);
  return `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`;
}

function applyTokens(t: TweakSettings): void {
  const root = document.documentElement;
  const v = VARIANTS[t.variant] ?? VARIANTS.glacier;
  Object.entries(v).forEach(([k, val]) => root.style.setProperty(k, val));
  root.style.setProperty("--accent", t.accent);
  root.style.setProperty("--accent-rgb", hexToRgb(t.accent));
  root.style.setProperty("--accent2", t.accent2);
  root.style.setProperty("--font-ui", `'${t.font}', system-ui, sans-serif`);
  root.style.setProperty("--cs", `${t.codeFontSize}px`);
  const rMap: Record<string, string> = { sharp: "4px", rounded: "14px", pill: "999px" };
  const rSm: Record<string, string>  = { sharp: "3px", rounded: "8px",  pill: "20px" };
  root.style.setProperty("--r",    rMap[t.radius] ?? "14px");
  root.style.setProperty("--r-sm", rSm[t.radius]  ?? "8px");
  document.body.dataset.btn  = t.btnStyle ?? "gradient";
  document.body.dataset.glow = t.glow !== false ? "on" : "off";

  if (t.lightMode) {
    root.setAttribute("data-theme", "light");
    root.style.setProperty("--bg",   "#f0f7ff");
    root.style.setProperty("--bg-rgb", "240,247,255");
    root.style.setProperty("--s0",   "#e4eef8");
    root.style.setProperty("--s1",   "#d5e6f2");
    root.style.setProperty("--t1",   "#0c1a2e");
    root.style.setProperty("--t2",   "#1e4060");
    root.style.setProperty("--t3",   "#4a7a9b");
    root.style.setProperty("--bd",   "rgba(2,132,199,.13)");
    root.style.setProperty("--bdb",  "rgba(2,132,199,.24)");
    root.style.setProperty("--card", "rgba(2,132,199,.04)");
  } else {
    root.removeAttribute("data-theme");
  }
}

export function useTweaks() {
  const [tweaks, setTweaksState] = useState<TweakSettings>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) return { ...DEFAULT_TWEAKS, ...JSON.parse(stored) };
    } catch { /* ignore */ }
    return DEFAULT_TWEAKS;
  });

  useEffect(() => { applyTokens(tweaks); }, [tweaks]);

  const setTweak = useCallback(<K extends keyof TweakSettings>(key: K, value: TweakSettings[K]) => {
    setTweaksState((prev) => {
      const next = { ...prev, [key]: value };
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    setTweaksState(DEFAULT_TWEAKS);
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  }, []);

  return { tweaks, setTweak, reset };
}
